import { ArrowDown, Crown, Plus, RotateCcw } from 'lucide-react';
import { Link } from 'react-router';
import { BRAND } from '@shared/constants/brand';
import { buildShareText, shortLabel } from '@shared/game/logic';
import { SELECTION_STAGES, STAGE_LABELS } from '@shared/types/room';
import { AdSlot } from '@/components/AdSlot';
import { Confetti } from '@/components/Confetti';
import { ShareButtons } from '@/components/ShareButtons';
import { useRoomStore } from '@/stores/roomStore';

export function ResultView() {
  const state = useRoomStore((s) => s.state);
  const selfId = useRoomStore((s) => s.selfId);
  const send = useRoomStore((s) => s.send);
  if (!state || !state.result) return null;

  const { result } = state;
  const isHost = state.hostId === selfId;
  const venueOnly = result.decidedAtStage === 'SUB_CATEGORY';
  const shareText = buildShareText(BRAND.name, result.path, result.menuName, result.decidedAtStage);

  return (
    <div className="relative flex flex-col gap-4 animate-fade-up">
      <Confetti />
      <div className="card relative overflow-hidden p-6 text-center sm:p-8">
        <p className="text-sm font-bold text-primary-600">{venueOnly ? '🎉 오늘 점심 가게 결정!' : '🎉 오늘 점심 결정!'}</p>
        <div className="mt-4 text-7xl animate-pop-in" aria-hidden="true">
          {result.emoji}
        </div>
        <h1 className="mt-3 text-3xl font-extrabold break-keep sm:text-4xl">{result.menuName}</h1>
        <p className="mt-2 text-charcoal-500">{venueOnly ? `"오늘은 ${result.menuName} 먹으러 가요!"` : `"오늘은 ${result.menuName} 먹는 날!"`}</p>
        {venueOnly && result.suggestedMenus.length > 0 && (
          <div className="mt-5 rounded-2xl bg-cream-100 px-4 py-4 text-left">
            <p className="text-sm font-semibold">메뉴는 각자 골라 드세요 🙌</p>
            <p className="mt-0.5 text-xs text-charcoal-500">이런 메뉴가 있어요</p>
            <ul className="mt-2.5 flex flex-wrap gap-1.5" aria-label="참고 메뉴">
              {result.suggestedMenus.map((m) => (
                <li key={m.id} className="rounded-full bg-white px-2.5 py-1 text-sm">
                  {m.name}
                </li>
              ))}
            </ul>
          </div>
        )}

        <ol className="mx-auto mt-6 flex max-w-xs flex-col items-center gap-1" aria-label="선택 과정">
          {result.path.map((s, i) => (
            <li key={s.stage} className="flex flex-col items-center">
              {i > 0 && <ArrowDown size={16} className="my-0.5 text-charcoal-500/50" aria-hidden="true" />}
              <span className="inline-flex items-center gap-1.5 rounded-full bg-cream-100 px-3 py-1 text-sm font-medium">
                <span aria-hidden="true">{s.emoji}</span>
                {shortLabel(s)}
              </span>
            </li>
          ))}
        </ol>
      </div>

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-semibold text-charcoal-700">누가 선택했을까요?</h2>
        <ol className="flex flex-col gap-2">
          {SELECTION_STAGES.map((stage, i) => {
            const s = result.path.find((p) => p.stage === stage);
            if (!s) return null;
            return (
              <li key={stage} className="flex items-center justify-between gap-3 rounded-xl bg-cream-50 px-3 py-2.5 text-sm">
                <span className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-white">{i + 1}</span>
                  <span className="text-charcoal-500">{STAGE_LABELS[stage].title}</span>
                </span>
                <span className="flex items-center gap-1.5 text-right">
                  <span className="font-semibold">{s.participantNickname}</span>
                  <span className="text-charcoal-500">-</span>
                  <span>{shortLabel(s)}</span>
                  {s.isRandom && (
                    <span className="rounded-full bg-accent-300/60 px-1.5 py-0.5 text-[10px] font-semibold" title={s.isAuto ? '연결 끊김으로 자동 선택' : '랜덤 선택'}>
                      {s.isAuto ? '자동' : '랜덤'}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="card flex flex-col gap-3 p-5">
        <ShareButtons title={BRAND.name} text={shareText} copyLabel="결과 텍스트 복사" shareLabel="결과 공유하기" size="lg" />
        {isHost ? (
          <button type="button" onClick={() => send({ type: 'restart' })} className="btn-primary btn-lg w-full">
            <RotateCcw size={20} aria-hidden="true" />
            다시 정하기
          </button>
        ) : (
          <p className="flex items-center justify-center gap-1.5 rounded-2xl bg-cream-100 py-3 text-sm text-charcoal-500">
            <Crown size={14} className="text-accent-500" aria-hidden="true" />
            방장이 "다시 정하기"를 누르면 새 게임이 시작돼요
          </p>
        )}
        <Link to="/create" className="btn-secondary btn-md w-full">
          <Plus size={18} aria-hidden="true" />
          새로운 방 만들기
        </Link>
      </div>

      <AdSlot name="result" />
    </div>
  );
}
